/**
 * Печатная страница `/r/:owner/:repo/print?<PrintOptions>`.
 *
 * Чистая, типографически-оптимизированная вёрстка без интерактива:
 * без TopBar, без sidebar, без Collapse — всё развёрнуто, плоским потоком.
 * После загрузки snapshot и шрифтов автоматически вызывает `window.print()`.
 *
 * Сам Ctrl+P на этой странице НЕ перехватывается — пользователь может
 * повторно открыть нативный диалог печати без модала.
 */

import { useEffect } from 'react';
import { Alert, Center, Loader, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { FRAME_IDS } from '@pflow/shared/v2';
import type {
    CanvasView, HealthAspect, NarrativeAspect, ProjectNarrativeAspectId, Snapshot,
} from '@pflow/shared/v2';
import { loadSnapshot } from '../lib/snapshot-loader';
import { fetchCommits, type CommitInfo } from '../lib/api-client';
import { fromSearchParams } from '../lib/print-options';
import type { PrintOptions } from '../lib/print-options';
import { MarkdownText } from '../components/MarkdownText';
import { NarrativeBlockView } from '../components/NarrativeBlockView';
import { aspectShowsFields } from '../components/NarrativeAspectView';

export function PrintPage(): JSX.Element {
    const { owner, repo } = useParams<{ owner: string; repo: string }>();
    const [searchParams] = useSearchParams();
    const opts = fromSearchParams(searchParams);
    const ref  = searchParams.get('ref')  ?? undefined;
    const path = searchParams.has('path') ? (searchParams.get('path') ?? '') : undefined;

    const snapshotQ = useQuery({
        queryKey: ['snapshot', owner, repo, ref, path],
        queryFn:  () => loadSnapshot({ owner: owner!, repo: repo!, ref, path }),
        enabled:  !!owner && !!repo,
        retry:    false,
    });

    const commitsQ = useQuery({
        queryKey: ['commits', owner, repo, ref, path, opts.historyDepth],
        queryFn:  () => fetchCommits({ owner: owner!, repo: repo!, ref, path, limit: opts.historyDepth }),
        // Загружаем только если секция реально включена.
        enabled:  !!owner && !!repo && opts.historyDepth > 0,
        retry:    false,
    });

    // Автозапуск печати после загрузки всех нужных данных и шрифтов.
    // Если включена «История изменений» — ждём ещё и commitsQ; если она
    // упала, всё равно печатаем (в Document будет inline-сообщение об ошибке),
    // а не блокируем print диалог из-за дополнительной секции.
    const dataReady =
        !!snapshotQ.data &&
        (opts.historyDepth === 0 || commitsQ.isFetched);

    useEffect(() => {
        if (!dataReady) return;
        const ready = (typeof document !== 'undefined' && document.fonts)
            ? document.fonts.ready
            : Promise.resolve();
        let cancelled = false;
        void ready.then(() => {
            if (cancelled) return;
            setTimeout(() => window.print(), 50);
        });
        return () => { cancelled = true; };
    }, [dataReady]);

    if (!owner || !repo) {
        return <Center mih="60vh"><Text c="dimmed">Нет owner/repo в URL.</Text></Center>;
    }
    if (snapshotQ.isLoading) return <Center mih="60vh"><Loader /></Center>;
    if (snapshotQ.error) {
        return (
            <Alert color="red" title="Ошибка загрузки snapshot" m="lg">
                {(snapshotQ.error as Error).message}
            </Alert>
        );
    }
    if (!snapshotQ.data) return <Center mih="60vh"><Text c="dimmed">Нет данных.</Text></Center>;

    return (
        <PrintDocument
            snapshot={snapshotQ.data}
            opts={opts}
            currentRef={ref}
            commits={commitsQ.data?.commits}
            commitsError={commitsQ.error ? (commitsQ.error as Error).message : null}
        />
    );
}

function PrintDocument({ snapshot, opts, currentRef, commits, commitsError }: {
    snapshot:     Snapshot;
    opts:         PrintOptions;
    currentRef:   string | undefined;
    commits:      CommitInfo[] | undefined;
    commitsError: string | null;
}): JSX.Element {
    const frame    = opts.frame     ? snapshot.projectAspects.filter(a => FRAME_IDS.has(a.id as ProjectNarrativeAspectId)) : [];
    const backstage = opts.backstage ? snapshot.projectAspects.filter(a => !FRAME_IDS.has(a.id as ProjectNarrativeAspectId)) : [];
    const canvases = opts.canvases.length === 0
        ? snapshot.canvases
        : snapshot.canvases.filter(c => opts.canvases.includes(c.name));

    return (
        <div className="pflow-print-page">
            {opts.cover && <Cover snapshot={snapshot} currentRef={currentRef} canvasCount={canvases.length} />}

            <UnitHeader snapshot={snapshot} hasCover={opts.cover} />

            {frame.length > 0 && (
                <section>
                    <h2>О проекте</h2>
                    <Stack gap="md">
                        {frame.map(a => <PrintAspect key={a.id} aspect={a} opts={opts} />)}
                    </Stack>
                </section>
            )}

            {canvases.map(c => (
                <CanvasSection key={c.name} canvas={c} opts={opts} />
            ))}

            {backstage.length > 0 && (
                <section style={{ marginTop: 24 }}>
                    <h2>Поддержка</h2>
                    <Stack gap="md">
                        {backstage.map(a => <PrintAspect key={a.id} aspect={a} opts={opts} />)}
                    </Stack>
                </section>
            )}

            {opts.showHealth && snapshot.health.length > 0 && (
                <section style={{ marginTop: 24 }}>
                    <h2>Здоровье модели</h2>
                    <Stack gap="md">
                        {snapshot.health.filter(h => h.count > 0).map(h => (
                            <HealthSection key={h.id} health={h} />
                        ))}
                    </Stack>
                </section>
            )}

            {opts.historyDepth > 0 && (
                <HistorySection commits={commits} error={commitsError} limit={opts.historyDepth} />
            )}
        </div>
    );
}

function Cover({ snapshot, currentRef, canvasCount }: {
    snapshot: Snapshot; currentRef: string | undefined; canvasCount: number;
}): JSX.Element {
    const title = snapshot.unit?.name ?? `${snapshot.meta.owner}/${snapshot.meta.repo}`;
    const shortSha = snapshot.meta.sha.slice(0, 7);
    // Используем ref из URL пользователя если задан, иначе ветку из snapshot meta
    // (это та ветка, на которой реально лежит SHA), иначе ничего.
    const branchLabel = currentRef ?? snapshot.meta.branch;
    // Дата коммита (author date) точнее, чем generatedAt: snapshot собирается
    // позже коммита, а пользователю интересно «на каком моменте проекта отчёт».
    const dateIso = snapshot.meta.commitDate ?? snapshot.meta.generatedAt;
    return (
        <section className="pflow-print-aspect" style={{ marginBottom: 32 }}>
            <Text fz="xs" c="dimmed" tt="uppercase" mb={4}>Документ проекта</Text>
            <h1>{title}</h1>
            <Text fz="sm" c="dimmed">
                {snapshot.meta.owner}/{snapshot.meta.repo}
                {branchLabel ? ` · ${branchLabel}` : ''}
                {' · '}
                <code>{shortSha}</code>
                {' · '}
                {formatDate(dateIso)}
                {' · '}
                {canvasCount} Канвас(а)
            </Text>
            {snapshot.meta.commitMessage && (
                <Text fz="sm" mt={6} fs="italic">
                    «{snapshot.meta.commitMessage}»
                </Text>
            )}
        </section>
    );
}

function UnitHeader({ snapshot, hasCover }: {
    snapshot: Snapshot; hasCover: boolean;
}): JSX.Element | null {
    if (!snapshot.unit) return null;
    return (
        <section className="pflow-print-aspect" style={{ marginBottom: 16 }}>
            {!hasCover && <h1>Юнит «{snapshot.unit.name}»</h1>}
            {snapshot.unit.metrics.length > 0 && (
                <Stack gap={4}>
                    {snapshot.unit.metrics.map((m, i) => (
                        <Text key={i} fz="sm">
                            <Text span fz="xs" c="dimmed" tt="uppercase" mr={6}>{m.label}</Text>
                            <Text span fw={600}>{m.value}</Text>
                        </Text>
                    ))}
                </Stack>
            )}
        </section>
    );
}

function CanvasSection({ canvas, opts }: { canvas: CanvasView; opts: PrintOptions }): JSX.Element {
    return (
        <section className="pflow-print-canvas" style={{ marginTop: 24 }}>
            <h2>🎨 Канвас «{canvas.name}»</h2>
            {canvas.metrics.length > 0 && (
                <Stack gap={2} mb={8}>
                    {canvas.metrics.map((m, i) => (
                        <Text key={i} fz="sm" c="dimmed">
                            <strong>{m.label}:</strong> {m.value}
                        </Text>
                    ))}
                </Stack>
            )}
            <Stack gap="md">
                {canvas.aspects.map(a => <PrintAspect key={a.id} aspect={a} opts={opts} />)}
            </Stack>
        </section>
    );
}

function PrintAspect({ aspect, opts }: { aspect: NarrativeAspect; opts: PrintOptions }): JSX.Element | null {
    const hasSummary = aspect.summary !== null;
    const hasBlocks  = aspect.blocks.length > 0;
    if (!hasSummary && !hasBlocks) return null;
    return (
        <article className="pflow-print-aspect">
            <h3>{aspect.title}</h3>
            {hasSummary && (
                <>
                    <MarkdownText text={aspect.summary!.text} />
                    {opts.showMeta && (
                        <Text fz="xs" c="dimmed" mt={2}>
                            {aspect.summary!.generatedBy} · {formatDate(aspect.summary!.generatedAt)}
                        </Text>
                    )}
                </>
            )}
            {opts.depth === 'full' && hasBlocks && (
                <Stack gap={4} mt={hasSummary ? 8 : 0}>
                    {aspect.blocks.map((b, i) => (
                        <NarrativeBlockView key={i} block={b} showFields={aspectShowsFields(aspect.id)} />
                    ))}
                </Stack>
            )}
        </article>
    );
}

function HistorySection({ commits, error, limit }: {
    commits: CommitInfo[] | undefined;
    error:   string | null;
    limit:   number;
}): JSX.Element {
    return (
        <section className="pflow-print-canvas" style={{ marginTop: 24 }}>
            <h2>История изменений</h2>
            <Text fz="xs" c="dimmed" mb={8}>
                Коммиты, затрагивающие <code>.pflow/</code> и <code>*.pflow</code>/<code>*.sflow</code>
                {' '}файлы. Последние {limit}.
            </Text>
            {error && (
                <Text fz="sm" c="red">Не удалось загрузить историю: {error}</Text>
            )}
            {!error && commits && commits.length === 0 && (
                <Text fz="sm" c="dimmed">Изменений по pflow/sflow файлам не найдено.</Text>
            )}
            {commits && commits.length > 0 && (
                <Stack gap={6}>
                    {commits.map(c => (
                        <article key={c.sha} className="pflow-print-aspect" style={{ paddingLeft: 8, borderLeft: '2px solid #ccc' }}>
                            <Text fz="sm" fw={500} style={{ wordBreak: 'break-word' }}>
                                {c.message}
                            </Text>
                            <Text fz="xs" c="dimmed">
                                <code>{c.sha.slice(0, 7)}</code> · {formatDateTime(c.date)} · {c.author}
                            </Text>
                        </article>
                    ))}
                </Stack>
            )}
        </section>
    );
}

function HealthSection({ health }: { health: HealthAspect }): JSX.Element {
    return (
        <article className="pflow-print-aspect">
            <h3>{health.title} <Text span fz="sm" c="dimmed">({health.count})</Text></h3>
            <Stack gap={4}>
                {health.findings.map((f, i) => (
                    <Text key={i} fz="sm">
                        <Text span fw={600}>«{f.entity.name}»</Text>
                        <Text span c="dimmed"> · {f.entity.type} · </Text>
                        {f.message}
                    </Text>
                ))}
            </Stack>
        </article>
    );
}

function formatDate(iso: string): string {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toISOString().slice(0, 10);
    } catch {
        return iso;
    }
}

function formatDateTime(iso: string): string {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        // YYYY-MM-DD HH:MM — компактно, читабельно в печатной строке
        return d.toISOString().slice(0, 16).replace('T', ' ');
    } catch {
        return iso;
    }
}
